<template>
  <div>
    <v-dialog v-model="reportLoading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Downloading Task Relationship Report...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-data-table-server
      :headers="headers"
      :items="items"
      style="border: solid 1px;"
      class="mb-4"
      show-select
      :loading="loading"
      :items-length="totalItems"
      @update:options="updateOptions"
    >
      <template v-slot:header.data-table-select="{ on, props }">
        <v-checkbox
          color="green"
          :model-value="selected && selected.length === 0 ? false : selected && totalItems === selected.length"
          @update:modelValue="onMultiSelectChange($event)"
          :indeterminate="selected && selected.length > 0 && totalItems !== selected.length"
        ></v-checkbox>
      </template>
      <template v-slot:top>
        <v-row dense class="me-3 mt-3" justify="end">
          <v-tooltip text="Export Task Relationship Report" location="bottom" v-if="isEditing && isITSupportOrProcessManager">
              <template v-slot:activator="{ props }">
                <v-icon @click="downloadTaskRelationshipReport" v-bind="props">mdi-tray-arrow-down</v-icon>
              </template>
            </v-tooltip>
        </v-row>
        <v-row dense class="px-3">
          <v-col cols="12">
            <v-autocomplete
              :items="allCriteriaForAutocomplete"
              variant="underlined"
              item-title="label"
              item-value="valueId"
              label="Search"
              hide-no-data
              v-model="search"
              hide-selected
              clearable
            ></v-autocomplete>
          </v-col>
        </v-row>
        <slot name="top" :selected="selected" :relationships="items"></slot>
      </template>
      <template v-slot:item="props">
        <tr>
          <td>
            <v-checkbox
              color="green"
              v-model="props.item.isSelected"
              hide-details
              @update:modelValue="onItemCheckboxChange($event, props.item)"
            ></v-checkbox>
          </td>
          <td>
            <div v-for="(label, index) in props.item.namedCriteria" :key="index">
              <a @click="edit(props.item)" class="blue-link">{{ label }}</a>
              <span v-if="index + 1 !== props.item.namedCriteria.length">&nbsp;&</span>
            </div>
          </td>
          <td>{{ props.item.totalApprovers }}</td>
          <td>{{ props.item.totalFyis }}</td>
          <td>{{ props.item.totalTasks }}</td>
          <td>
            <a href="#" @click.prevent="clone(props.item)">Clone</a>
          </td>
          <td>
            <v-icon @click="askDeleteConfirmation(props.item)" color="red">mdi-delete-outline</v-icon>
          </td>
        </tr>
      </template>
    </v-data-table-server>
    <v-dialog persistent v-model="showDeleteConfirmationDialog" max-width="500">
      <v-card v-if="showDeleteConfirmationDialog">
        <v-container>
          <h3>Are you sure you want to delete this response relationship?</h3>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="deleteRelationship">Yes</v-btn>
            <v-btn color="black" text @click="cancelDeletion">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AdminService from '@/services/admin.service'

export default {
  name: 'RelationshipTable',
  props: {
    questions: Array,
    isEditing: Boolean,
    relationships: Array,
    communityAbbreviation: String,
    isITSupportOrProcessManager: Boolean
  },
  data() {
    return {
      headers: [
        {
          title: 'F/R Criteria',
          key: 'namedCriteria'
        },
        {
          title: this.$t('Approvers'),
          key: 'approvers'
        },
        {
          title: this.$t('FYIs'),
          key: 'fyis'
        },
        {
          title: this.$t('Tasks'),
          key: 'tasks'
        },
        {
          title: '',
          sortable: false
        },
        {
          title: '',
          sortable: false
        }
      ],
      selected: [],
      selectedForDeletion: {},
      showDeleteConfirmationDialog: false,
      search: '',
      dataTableOptions: {},
      items: [],
      totalItems: 0,
      loading: false,
      parsedRelationships: [],
      reportLoading: false
    }
  },
  computed: {
    allCriteriaForAutocomplete() {
      return this.questions.reduce((acc, cur) => {
        acc.push({
          label: `${cur.parentQuestion.fullLabel}${cur.label ? ` - ${cur.label}` : ''}`,
          valueId: cur.id || cur.clientId
        })
        return acc
      }, [])
    }
  },
  methods: {
    onMultiSelectChange(value) {
      if (value) {
        this.selected = this.items
      } else {
        this.selected = []
      }
      this.items = this.items.map(i => ({ ...i, isSelected: value }))
    },
    edit(relationship) {
      this.$emit('edit', relationship)
    },
    getCurrentQuestion(criterion) {
      const question = this.questions.find(q => q.parentQuestion.clientId === criterion.questionId)
      return question ? question.parentQuestion : {}
    },
    getCurrentValue(criterion, question) {
      if (question) {
        return question.values.find(v => v.clientId === criterion.valueId) || {}
      }

      return {}
    },
    askDeleteConfirmation(relationship) {
      this.selectedForDeletion = relationship
      this.showDeleteConfirmationDialog = true
    },
    deleteRelationship() {
      this.$emit('delete', this.selectedForDeletion)
      this.selectedForDeletion = {}
      this.showDeleteConfirmationDialog = false
    },
    cancelDeletion() {
      this.selectedForDeletion = {}
      this.showDeleteConfirmationDialog = false
    },
    clone(relationship) {
      this.$emit('clone', relationship)
    },
    onItemCheckboxChange(value, item) {
      if (value) {
        this.selected.push(item)
      } else {
        this.selected = this.selected.filter(s => s.clientId !== item.clientId)
      }
    },
    cleanSelected() {
      this.selected = []
    },
    sortAndFilterItems(items, search) {
      let sort = 'asc'
      if (this.dataTableOptions.sortDesc) {
        sort = this.dataTableOptions.sortDesc.map(s => (s ? 'desc' : 'asc'))
      }

      return this.$_.orderBy(items, this.dataTableOptions.sortBy, sort).filter(item =>
        search && search.trim() ? item.valueId.includes(search.trim()) : true
      )
    },
    paginateItems(items) {
      const begin = (this.dataTableOptions.page - 1) * this.dataTableOptions.itemsPerPage
      const end = (this.dataTableOptions.page - 1) * this.dataTableOptions.itemsPerPage + this.dataTableOptions.itemsPerPage
      return items.slice(begin, end)
    },
    getItems(query) {
      if (!this.isEditing) {
        const filteredItems = this.sortAndFilterItems(this.parsedRelationships, query)
        this.totalItems = filteredItems.length
        this.items = this.paginateItems(filteredItems)
        return
      }
      const { itemsPerPage, page, sortBy, sortDesc } = this.dataTableOptions
      this.loading = true
      AdminService.getPaginatedQARelationships(this.communityAbbreviation, {
        limit: itemsPerPage,
        page,
        sortBy,
        sortDesc,
        query
      })
        .then(result => {
          this.items = result.items.map(r => ({
            ...r,
            clientId: r.relationshipId,
            isSelected: this.selected.findIndex(s => s.clientId === r.relationshipId) !== -1
          }))
          this.totalItems = result.totalItems
        })
        .finally(() => (this.loading = false))
    },
    updateOptions(options) {
      console.log(`config community relationship table ${JSON.stringify(options, null, 4)}`)
      this.dataTableOptions = options
      this.getItems(this.search)
    },
    refreshItems(items) {
      this.parsedRelationships = (items || []).map(r => {
        r.valueId = []
        r.namedCriteria = r.criteria.reduce((acc, cur) => {
          const question = this.getCurrentQuestion(cur)
          const value = this.getCurrentValue(cur, question)
          const label = question ? question.fullLabel : ''
          acc = acc.concat(`${label} - ${value.enabled ? value.label : '(Disabled) ' + value.label}`)
          r.valueId.push(cur.valueId)
          return acc
        }, [])

        r.totalFyis = r.fyis.length
        r.totalApprovers = r.approvers.length
        r.totalTasks = r.tasks.length
        r.isSelected = this.selected.findIndex(x => x.clientId === r.clientId) > -1

        return r
      })
    },
    downloadTaskRelationshipReport() {
      this.reportLoading = true
      AdminService.getTaskRelationshipReport(this.communityAbbreviation)
        .catch(e => console.error(e))
        .finally(() => (this.reportLoading = false))
    }
  },
  watch: {
    selected(newValue) {
      if (!newValue) {
        return []
      }
      this.$emit('input', newValue)
    },
    search(newValue) {
      this.getItems(newValue)
    },
    relationships(newValue) {
      if (this.isEditing) {
        this.getItems(this.search)
        return
      }

      this.refreshItems(newValue)
      this.getItems(this.search)
    }
  }
}
</script>
